/**
 * Billing Service — Doctif
 * Connects to backend billing API endpoints for invoices, payments, treatments.
 */
import { fetchApi } from '../utils/api';

// ── Types ──────────────────────────────────────────────────

export interface InvoiceItem {
  id?: string;
  act_code: string;
  description: string;
  tooth_number: number | null;
  quantity: number;
  unit_price: number;
  total: number;
  clinical_treatment_id?: string | null;
}

export interface Invoice {
  id: string;
  patient_id: string;
  patient_name: string;
  invoice_number: string;
  date: string;
  due_date: string | null;
  total_amount: number;
  discount: number;
  amount_paid: number;
  amount_due: number;
  status: 'Draft' | 'Outstanding' | 'Partial' | 'Paid' | 'Overdue' | 'Cancelled';
  invoice_type: 'Estimate' | 'Final' | 'Correction';
  notes: string;
  created_at: string;
  updated_at: string;
  items?: InvoiceItem[];
  payments?: PaymentRecord[];
}

export interface PaymentRecord {
  id: string;
  invoice_id?: string;
  invoice_number?: string;
  patient_id?: string;
  patient_name?: string;
  amount: number;
  date: string;
  method: string;
  receipt_number: string;
  notes: string;
}

export interface BillingSummary {
  total_invoices: number;
  total_outstanding: number;
  total_overdue: number;
  total_paid_this_month: number;
  total_revenue_ytd: number;
  collection_rate: number;
  pending_insurance_claims: number;
}

export interface ClinicalTreatmentBilling {
  id: string;
  patient_id: string;
  patient_name: string;
  act_code: string;
  act_label: string;
  category_name: string;
  tooth_number: number | null;
  surfaces: string;
  status: string;
  amount: number;
  completed_date: string | null;
  planned_date: string | null;
  notes: string;
  is_invoiced: boolean;
  created_at: string;
}

export interface PatientBilling {
  patient: {
    id: string;
    name: string;
    insurance: string;
    cin_number: string;
  };
  total_billed: number;
  total_paid: number;
  outstanding_balance: number;
  invoices: Invoice[];
  recent_payments: PaymentRecord[];
}

export interface BillingDocument {
  id: string;
  invoice_id: string | null;
  invoice_number: string | null;
  patient_id: string | null;
  patient_name: string | null;
  document_type: 'Invoice' | 'CNOPS' | 'Estimate' | 'Other';
  file_name: string;
  file_format: string;
  file_size: number;
  notes: string;
  generated_at: string;
}

export interface CreateInvoiceRequest {
  patient_id: string;
  treatment_ids?: string[];
  items?: Array<{
    act_code?: string;
    description: string;
    tooth_number?: number | null;
    quantity?: number;
    unit_price: number;
  }>;
  due_date?: string;
  notes?: string;
  invoice_type?: 'Estimate' | 'Final' | 'Correction';
  discount?: number;
  finalize?: boolean;
}

export interface CreatePaymentRequest {
  invoice_id: string;
  amount: number;
  date?: string;
  method?: string;
  receipt_number?: string;
  notes?: string;
}

// ── Service ────────────────────────────────────────────────

export const billingService = {
  // ── Summary ──
  async getBillingSummary(): Promise<BillingSummary> {
    const res = await fetchApi('/api/billing/summary/');
    return res;
  },

  // ── Invoices ──
  async getInvoices(filters?: {
    status?: string;
    patient_id?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
  }): Promise<Invoice[]> {
    let url = '/api/billing/invoices/';
    if (filters) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const qs = params.toString();
      if (qs) url += '?' + qs;
    }
    const res = await fetchApi(url);
    return res.invoices || [];
  },

  async getInvoice(invoiceId: string): Promise<Invoice> {
    const res = await fetchApi(`/api/billing/invoices/${invoiceId}/`);
    return res.invoice;
  },

  async createInvoice(data: CreateInvoiceRequest): Promise<{ success: boolean; invoice_id?: string; invoice_number?: string; message: string }> {
    return await fetchApi('/api/billing/invoices/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateInvoice(invoiceId: string, data: any): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`/api/billing/invoices/${invoiceId}/update/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async deleteInvoice(invoiceId: string): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`/api/billing/invoices/${invoiceId}/delete/`, {
      method: 'POST',
    });
  },

  async finalizeInvoice(invoiceId: string): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`/api/billing/invoices/${invoiceId}/finalize/`, {
      method: 'POST',
    });
  },

  // ── Payments ──
  async createPayment(data: CreatePaymentRequest): Promise<{ success: boolean; payment_id?: string; message: string }> {
    return await fetchApi('/api/billing/payment/create/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getPayments(filters?: { patient_id?: string }): Promise<PaymentRecord[]> {
    let url = '/api/billing/payments/';
    if (filters?.patient_id) url += `?patient_id=${filters.patient_id}`;
    const res = await fetchApi(url);
    return res.payments || [];
  },

  // ── Treatments ──
  async getTreatments(filters?: {
    patient_id?: string;
    status?: string;
    unbilled?: boolean;
  }): Promise<ClinicalTreatmentBilling[]> {
    let url = '/api/billing/treatments/';
    const params = new URLSearchParams();
    if (filters?.patient_id) params.append('patient_id', filters.patient_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.unbilled) params.append('unbilled', 'true');
    const qs = params.toString();
    if (qs) url += '?' + qs;
    const res = await fetchApi(url);
    return res.treatments || [];
  },

  async getUnbilledTreatments(patientId: string): Promise<ClinicalTreatmentBilling[]> {
    const res = await fetchApi(`/api/billing/unbilled-treatments/${patientId}/`);
    return res.treatments || [];
  },

  // ── Patient Billing ──
  async getPatientBilling(patientId: string): Promise<PatientBilling> {
    const res = await fetchApi(`/api/billing/patient/${patientId}/`);
    return res;
  },

  // ── CNOPS Insurance Form ──
  async downloadCNOPSForm(invoiceId: string): Promise<void> {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const url = `/api/billing/invoices/${invoiceId}/cnops-pdf/`;
    
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Tenant': subdomain,
      },
    });
    
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(err.error || 'Download failed');
    }
    
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    // Extract filename from Content-Disposition or use default
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    a.download = match ? match[1] : `CNOPS_${invoiceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  // ── Invoice Excel Download ──
  async downloadInvoiceExcel(invoiceId: string): Promise<void> {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const url = `/api/billing/invoices/${invoiceId}/invoice-excel/`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Tenant': subdomain,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(err.error || 'Download failed');
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    a.download = match ? match[1] : `Facture_${invoiceId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  // ── Billing Documents (file tracking) ──
  async getDocuments(filters?: {
    document_type?: string;
    patient_id?: string;
    invoice_id?: string;
    search?: string;
  }): Promise<BillingDocument[]> {
    let url = '/api/billing/documents/';
    if (filters) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const qs = params.toString();
      if (qs) url += '?' + qs;
    }
    const res = await fetchApi(url);
    return res.documents || [];
  },

  async deleteDocument(docId: string): Promise<{ success: boolean; message: string }> {
    return await fetchApi(`/api/billing/documents/${docId}/delete/`, {
      method: 'POST',
    });
  },

  async regenerateInvoiceExcel(invoiceId: string): Promise<void> {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const url = `/api/billing/documents/${invoiceId}/regenerate-invoice/`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-Tenant': subdomain },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(err.error || 'Download failed');
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    a.download = match ? match[1] : `Facture_${invoiceId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },

  async regenerateCNOPSForm(invoiceId: string): Promise<void> {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const url = `/api/billing/documents/${invoiceId}/regenerate-cnops/`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-Tenant': subdomain },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(err.error || 'Download failed');
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    a.download = match ? match[1] : `CNOPS_${invoiceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  },
};